<template>
    <yams-template>
        <template #header>
            <div class="mr-5">
                <h4 class="text-primary font-weight-bold">Calendrier</h4>
            </div>
            <div class="mr-4">
                <NavOperations></NavOperations>
            </div>
        </template>
        <template #content>
            <ProgressBar v-if="isLoading" mode="indeterminate" style="height: 6px; margin-bottom: 10px">
            </ProgressBar>
            <div class="card card-body p-1" id="mapCalendar">
                <FullCalendar ref="fullCalendar" :options='calendarOptions'>
                    <template v-slot:eventContent='arg'>
                        <p href="#" class="btn text-wrap text-white"
                            @click.prevent="showEvent(arg.event.extendedProps)">
                            {{ arg.event.title }}
                        </p>
                    </template>
                </FullCalendar>
            </div>
        </template>
    </yams-template>
</template>

<script>
import moment from 'moment'
import { defineComponent, defineAsyncComponent } from 'vue';
const ShowCourse = defineAsyncComponent(() => import('../courses/ShowCourse.vue'));
const ShowIntervention = defineAsyncComponent(() => import('@/views/parc-auto/interventions/ShowIntervention.vue'));
const ShowDocument = defineAsyncComponent(() => import('@/views/calendrier/ShowDocument.vue'));
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import NavOperations from '../NavOperations.vue';
import { ApiService } from '@/services/ApiService';

export default defineComponent({
    components: { FullCalendar, NavOperations },
    data() {
        return {
            isLoading: false,
            lastID: 1,
            currentPeriod: null,
            events: [],
            calendarOptions: {
                plugins: [dayGridPlugin, bootstrapPlugin],
                themeSystem: 'bootstrap',
                initialView: 'dayGridMonth',
                events: [],
                locale: 'fr',
                height: 'auto',
                datesSet: this.handleMonthChange,
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: false
                }
            }
        }
    },
    mounted() {
        this.refreshDatas(moment().format('YYYY-MM'));
    },
    methods: {
        handleMonthChange(payload) {
            const newDate = payload.view.currentStart;
            const mois = String(newDate.getMonth() + 1).padStart(2, '0'); // Format à deux chiffres
            const annee = newDate.getFullYear();
            const newPeriod = `${annee}-${mois}`;

            // Ne rafraîchir que si on change de période
            if (this.currentPeriod !== newPeriod) {
                this.currentPeriod = newPeriod;
                this.refreshDatas(newPeriod);
            }
        },

        async refreshDatas(period) {
            this.isLoading = true;
            this.clearEvents();

            try {
                await Promise.all([
                    this.getCourses(period),
                    this.getInterventions(period),
                    this.getCamionDocuments(period)
                ]);
            } catch (error) {
                console.error('Erreur lors du chargement des données:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Problème de connexion',
                    detail: 'Une erreur s\'est produite lors du chargement des données',
                    life: 5000
                });
            } finally {
                this.isLoading = false;
            }
        },

        clearEvents() {
            this.events = [];
            this.calendarOptions.events = [];
            if (this.$refs.fullCalendar) {
                const calendarApi = this.$refs.fullCalendar.getApi();
                calendarApi.removeAllEvents();
            }
        },

        async getCourses(period) {
            try {
                const response = await this.axios.get('/api/courses?req_period=' + period);
                const newEvents = [];

                // Utiliser ApiService pour s'assurer que response.data est un tableau
                const courses = ApiService.extractApiData(response);

                courses.forEach(element => {
                    // Événement de départ
                    newEvents.push({
                        title: `Départ course ${element.camion?.plaque || 'N/A'} ${element.adresse_chargement || ''} - ${element.adresse_dechargement || ''}`,
                        date: element.date_chargement,
                        color: 'blue',
                        extendedProps: {
                            id: element.id,
                            table: 'courses',
                            data: element
                        },
                        id: `course-depart-${element.id}`
                    });

                    // Événement d'arrivée si disponible
                    if (element.date_dechargement) {
                        newEvents.push({
                            title: `Arrivée course ${element.camion?.plaque || 'N/A'} ${element.adresse_chargement || ''} - ${element.adresse_dechargement || ''}`,
                            date: element.date_dechargement,
                            color: 'green',
                            extendedProps: {
                                id: element.id,
                                table: 'courses',
                                data: element
                            },
                            id: `course-arrivee-${element.id}`
                        });
                    }
                });

                this.addEvents(newEvents);
            } catch (error) {
                console.error('Erreur lors du chargement des courses:', error);
                throw error;
            }
        },

        async getInterventions(period) {
            try {
                const response = await this.axios.get('/api/interventions?req_period=' + period);
                const newEvents = [];

                // Utiliser ApiService pour s'assurer que response.data est un tableau
                const interventions = ApiService.extractApiData(response);

                interventions.forEach(element => {
                    // Événement de début d'intervention
                    newEvents.push({
                        title: `Début ${element.type || 'Intervention'} ${element.camion?.plaque || 'N/A'}`,
                        date: element.debut,
                        color: 'pink',
                        extendedProps: {
                            id: element.id,
                            table: 'interventions',
                            data: element
                        },
                        id: `intervention-debut-${element.id}`
                    });

                    // Événement de fin d'intervention si disponible
                    if (element.fin) {
                        newEvents.push({
                            title: `Sortie ${element.type || 'Intervention'} ${element.camion?.plaque || 'N/A'}`,
                            date: element.fin,
                            color: '#ec8238',
                            extendedProps: {
                                id: element.id,
                                table: 'interventions',
                                data: element
                            },
                            id: `intervention-fin-${element.id}`
                        });
                    }
                });

                this.addEvents(newEvents);
            } catch (error) {
                console.error('Erreur lors du chargement des interventions:', error);
                throw error;
            }
        },

        async getCamionDocuments(period) {
            try {
                const response = await this.axios.get('/api/documents?req_period=' + period);
                const newEvents = [];

                // Utiliser ApiService pour s'assurer que response.data est un tableau
                const documents = ApiService.extractApiData(response);

                documents.forEach(element => {
                    if (element.camion_id !== null || element.user_id !== null) {
                        newEvents.push({
                            title: `Expiration ${element.libelle || 'Document'} de ${element.association || 'N/A'}`,
                            date: element.date_expiration,
                            color: 'red',
                            extendedProps: {
                                id: element.id,
                                table: 'documents',
                                data: element
                            },
                            id: `document-${element.id}`
                        });
                    }
                });

                this.addEvents(newEvents);
            } catch (error) {
                console.error('Erreur lors du chargement des documents:', error);
                throw error;
            }
        },

        addEvents(newEvents) {
            // Ajouter au tableau local
            this.events = [...this.events, ...newEvents];

            // Mettre à jour le calendrier
            if (this.$refs.fullCalendar) {
                const calendarApi = this.$refs.fullCalendar.getApi();
                calendarApi.addEventSource(newEvents);
            } else {
                // Si le calendrier n'est pas encore rendu, ajouter aux options
                this.calendarOptions.events = [...this.calendarOptions.events, ...newEvents];
            }
        },

        showEvent(event) {
            if (!event || !event.table || !event.data) return;

            const modalProps = {
                header: `${this.getModalTitle(event)} ID${event.id}`,
                style: {
                    width: this.getModalWidth(event.table)
                },
                modal: true
            };

            let component;
            switch (event.table) {
                case 'courses':
                    component = ShowCourse;
                    break;
                case 'interventions':
                    component = ShowIntervention;
                    break;
                case 'documents':
                    component = ShowDocument;
                    break;
                default:
                    return;
            }

            this.$dialog.open(component, {
                props: modalProps,
                data: event.data
            });
        },

        getModalTitle(event) {
            switch (event.table) {
                case 'courses': return 'Course';
                case 'interventions': return 'Intervention';
                case 'documents': return 'Document';
                default: return 'Événement';
            }
        },

        getModalWidth(type) {
            switch (type) {
                case 'documents': return '30vw';
                default: return '50vw';
            }
        }
    }
})
</script>

<style scoped>
/* Styles pour améliorer l'apparence du calendrier */
:deep(.fc-event) {
    cursor: pointer;
}

:deep(.fc-toolbar-title) {
    font-size: 1.5rem !important;
    text-transform: capitalize;
}

:deep(.fc-daygrid-day-number) {
    font-weight: 500;
}

:deep(.fc-day-today) {
    background-color: rgba(0, 123, 255, 0.1) !important;
}

/* Style pour les tooltips */
.event-tooltip {
    max-width: 300px;
    padding: 10px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 9999;
}
</style>